/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import { ModalScrollScreen } from '../../../shared/layout/ModalScrollScreen'
import { useContentLayout } from '../../../shared/layout/useContentLayout';
import { OperationalConfirmModal } from '../../../shared/modals/OperationalConfirmModal';
import { ModuleEmptyState } from '../../../shared/ui/ModuleEmptyState';
import { UnderlineTabBar, type UnderlineTabOption } from '../../../shared/ui/UnderlineTabBar';
import { settingsStyles } from '../../../settings/screens/settingsScreen.styles';
import { ticketColors } from '../../../shared/theme/ticketColors'
import { spacing } from '../../../shared/theme/spacing';
import { useScreenLoadShell } from '../../../shared/ui/hooks-screen/useScreenLoad';
import { empPermStyles } from './employeePermissions/employeePermissions.styles';
import { useEmployeePermissions, type EmployeeTab } from './employeePermissions/useEmployeePermissions';
import { EmployeePermissionsToolbar } from './employeePermissions/EmployeePermissionsToolbar';
import { EmployeeActiveTab } from './employeePermissions/EmployeeActiveTab';
import { EmployeeRevokedTab } from './employeePermissions/EmployeeRevokedTab';
import { EmployeeInvitationsTab } from './employeePermissions/EmployeeInvitationsTab';
import { EmployeePermissionsMatrix } from './employeePermissions/EmployeePermissionsMatrix';
import { EmployeePermissionsTable } from './employeePermissions/EmployeePermissionsTable';
import { EmployeeDetailModal } from './employeePermissions/EmployeeDetailModal';
import type { MasterFilterOption } from '../../../shared/filters/MasterFilterSheetModal';
import type { ShellStackParamList } from '../../../shell/navigation/shellStackParamList';
import { CrecePaywallSheet } from '../../../shell/billing/components/CrecePaywallSheet';
import { crecePaywallTeamCopy } from '../../../../../../shared/plan-crece-copy';
import { trackCreceFunnel } from '../../../shell/billing/trackCreceFunnel';
import { CRECE_FUNNEL_EVENTS } from '../../../../../../shared/crece-funnel';

export const EmployeePermissionsScreen = () => {
  const { layoutWide } = useContentLayout('data');
  const { layoutWebShell } = useContentLayout('settings');
  const navigation = useNavigation();
  const route = useRoute<RouteProp<ShellStackParamList, 'EmployeePermissions'>>();
  const vm = useEmployeePermissions();
  const [teamPaywallVisible, setTeamPaywallVisible] = React.useState(false);

  useEffect(() => {
    const tab = route.params?.initialTab;
    if (tab) vm.setActiveTab(tab);
  }, [route.params?.initialTab, vm.setActiveTab]);

  const mobile = !layoutWebShell;

  const { refreshing, onRefresh: onRefreshEmployees } = useScreenLoadShell(
    'EmployeePermissions',
    async (opts) => {
      if (!vm.canViewTeam) return { silent: true };
      await vm.loadEmployees(opts);
    }
  );

  const tabOptions = useMemo(() => {
    const revokedCount = vm.employees.filter((e) => e.isActive === false).length;
    const pendingCount = vm.pendingInvitations.length;
    const items: UnderlineTabOption<EmployeeTab>[] = [
      { value: 'active', label: 'Activos' },
      {
        value: 'revoked',
        label: revokedCount > 0 ? `Revocados (${revokedCount})` : 'Revocados (0)',
      },
      {
        value: 'invitations',
        label: pendingCount > 0 ? `Invitaciones (${pendingCount})` : 'Invitaciones',
        attention: pendingCount > 0,
      },
    ];
    return items;
  }, [vm.employees, vm.pendingInvitations.length]);

  const branchOptions: MasterFilterOption[] = useMemo(
    () => vm.branches.map((b) => ({ id: b.id, name: b.name })),
    [vm.branches]
  );

  if (vm.entLoading) {
    return (
      <View style={[styles.container, { justifyContent: 'center', padding: spacing.lg }]}>
        <ActivityIndicator size="large" color={ticketColors.gold} />
        <Text style={[styles.subtitle, { textAlign: 'center', marginTop: spacing.md }]}>
          Cargando cuenta…
        </Text>
      </View>
    );
  }

  if (!vm.canViewTeam) {
    const blockedByPlan = vm.entitlements?.allow_employees === false;
    return (
      <>
        <ModalScrollScreen variant={mobile ? 'settings' : 'settingsWide'} contentContainerStyle={empPermStyles.content}>
          <View style={[settingsStyles.card, empPermStyles.emptyState]}>
            <Text style={empPermStyles.emptyTitle}>
              {blockedByPlan ? crecePaywallTeamCopy.title : 'No disponible'}
            </Text>
            <Text style={empPermStyles.emptySubtitle}>
              {!vm.entitlements
                ? 'No pudimos cargar el plan de tu cuenta. Reintentá en unos segundos.'
                : blockedByPlan
                  ? crecePaywallTeamCopy.body
                  : 'No tenés permiso para ver el equipo. Pedile acceso al dueño.'}
            </Text>
            {blockedByPlan ? (
              <TouchableOpacity
                style={styles.inviteHeaderButton}
                onPress={() => setTeamPaywallVisible(true)}
                accessibilityRole="button"
              >
                <Text style={styles.inviteHeaderButtonText}>{crecePaywallTeamCopy.cta}</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        </ModalScrollScreen>
        {blockedByPlan ? (
          <CrecePaywallSheet
            visible={teamPaywallVisible}
            copy={crecePaywallTeamCopy}
            onActivate={() => {
              setTeamPaywallVisible(false);
              trackCreceFunnel(CRECE_FUNNEL_EVENTS.ctaClick, { source: 'team_permissions_paywall' });
              navigation.navigate('ActivateCrece' as never);
            }}
            onDismiss={() => setTeamPaywallVisible(false)}
          />
        ) : null}
      </>
    );
  }

  const showToolbar = vm.activeTab !== 'invitations';
  const showViewToggle = vm.activeTab === 'active' && layoutWide;

  return (
    <>
      <ModalScrollScreen
        variant={mobile ? 'settings' : 'settingsWide'}
        contentContainerStyle={empPermStyles.content}
        accessibilityLabel="Pantalla de permisos por empleado"
        refreshing={refreshing}
        onRefresh={() => void onRefreshEmployees()}
        testID="perfil.team.permissions.screen"
      >
        {vm.employeesLoading && vm.employees.length === 0 ? (
          <ModuleEmptyState loading loadingText="Cargando empleados…" />
        ) : null}
        {vm.employeesError && !vm.employeesLoading ? (
          <Text style={empPermStyles.errorText}>{vm.employeesError}</Text>
        ) : null}

        {!vm.employeesLoading && vm.employees.length === 0 && vm.pendingInvitations.length === 0 ? (
          <ModuleEmptyState
            title="No hay empleados"
            subtitle="Invitá a alguien y aparece acá."
            primaryAction={{
              label: 'Invitar empleado',
              onPress: () => navigation.navigate('InviteEmployee' as never),
            }}
            testID="perfil.team.permissions.empty"
          />
        ) : (
          <>
            {vm.canInvite ? (
              <TouchableOpacity
                style={[
                  styles.inviteHeaderButton,
                  !layoutWebShell && styles.inviteHeaderButtonStretch,
                ]}
                onPress={() => navigation.navigate('InviteEmployee' as never)}
                accessibilityLabel="Invitar empleado"
                accessibilityRole="button"
                testID="perfil.team.permissions.invite-btn"
              >
                <Text style={styles.inviteHeaderButtonText}>Invitar empleado</Text>
              </TouchableOpacity>
            ) : null}

            <View style={styles.sectionToggleWrap}>
              <UnderlineTabBar
                options={tabOptions}
                value={vm.activeTab}
                onChange={vm.setActiveTab}
                testIDPrefix="perfil.team.permissions.tab"
              />
            </View>

            {showToolbar ? (
              <EmployeePermissionsToolbar
                searchQuery={vm.searchQuery}
                onSearchChange={vm.setSearchQuery}
                branchFilter={vm.branchFilter}
                onBranchFilterChange={vm.setBranchFilter}
                branchOptions={branchOptions}
                sortBy={vm.sortBy}
                onSortChange={vm.setSortBy}
                viewMode={vm.viewMode}
                onViewModeChange={vm.setViewMode}
                showViewToggle={showViewToggle}
                hasActiveFilters={vm.hasActiveFilters}
                onClearFilters={vm.clearFilters}
              />
            ) : null}

            <View style={empPermStyles.tabPane}>
              {vm.activeTab === 'active' ? (
                vm.viewMode === 'matrix' && layoutWide ? (
                  <EmployeePermissionsMatrix vm={vm} />
                ) : layoutWide ? (
                  <EmployeePermissionsTable vm={vm} />
                ) : (
                  <EmployeeActiveTab vm={vm} />
                )
              ) : null}

              {vm.activeTab === 'revoked' ? <EmployeeRevokedTab vm={vm} /> : null}

              {vm.activeTab === 'invitations' ? (
                <EmployeeInvitationsTab
                  vm={vm}
                  onNavigateInvite={() => navigation.navigate('InviteEmployee' as never)}
                />
              ) : null}
            </View>
          </>
        )}
      </ModalScrollScreen>

      {vm.selectedEmployee ? (
        <EmployeeDetailModal vm={vm} />
      ) : null}

      {vm.confirmModalProps ? (
        <OperationalConfirmModal
          visible
          title={vm.confirmModalProps.title}
          entityName={'entityName' in vm.confirmModalProps ? vm.confirmModalProps.entityName : undefined}
          message={vm.confirmModalProps.message}
          confirmLabel={vm.confirmModalProps.confirmLabel}
          destructive={vm.confirmModalProps.destructive}
          iconName={vm.confirmModalProps.iconName}
          loading={vm.confirmLoading}
          onClose={vm.closeConfirm}
          onConfirm={vm.handleConfirmAction}
        />
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ticketColors.bg,
  },
  subtitle: {
    ...settingsStyles.caption,
  },
  sectionToggleWrap: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
    backgroundColor: ticketColors.bg,
  },
  inviteHeaderButton: {
    alignSelf: 'flex-start',
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    minHeight: 44,
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    marginBottom: spacing.sm,
  },
  inviteHeaderButtonStretch: {
    alignSelf: 'stretch',
  },
  inviteHeaderButtonText: {
    ...settingsStyles.primaryButtonText,
    textAlign: 'center',
  },
});
